import ExcelJS from 'exceljs';

/**
 * Generates a comprehensive metrics input template Excel workbook with ExcelJS
 * Includes proper formatting, data validation, and dropdowns
 */
export const generateMetricsTemplate = async (processNames: string[] = [], opportunityNames: string[] = []) => {
  const workbook = new ExcelJS.Workbook();

  workbook.creator = 'Jarvis RCSA Platform';
  workbook.created = new Date();
  workbook.modified = new Date();

  // Create all tabs
  createInstructionsTab(workbook);
  createEngagementSetupTab(workbook);
  createLaborEconomicsTab(workbook);
  createProcessBaselineMetricsTab(workbook, processNames);
  createOpportunitySpecificInputsTab(workbook, opportunityNames, processNames);
  createNonFinancialBenefitsTab(workbook, opportunityNames);

  // Generate and download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'Jarvis_Process_Metrics_Template_v1.xlsx';
  link.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Tab 0: Instructions
 */
function createInstructionsTab(workbook: ExcelJS.Workbook) {
  const sheet = workbook.addWorksheet('Instructions', {
    views: [{ state: 'frozen', ySplit: 1 }]
  });

  // Title row
  sheet.getCell('A1').value = 'JARVIS PROCESS METRICS TEMPLATE - INSTRUCTIONS';
  sheet.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
  sheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
  sheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

  const instructions = [
    '',
    'OVERVIEW',
    'This template enables you to input real client data gathered from interviews and observations.',
    'All ROI, investment, and time savings calculations will be based on these empirical inputs.',
    '',
    'REQUIRED vs OPTIONAL FIELDS',
    '• Fields marked with * are REQUIRED - business case calculations depend on them',
    '• Optional fields improve accuracy but calculations will work without them',
    '',
    'HOW TO COMPLETE THIS TEMPLATE',
    '1. Start with "Engagement Setup" tab - enter client and project information',
    '2. Complete "Labor Economics" tab - enter hourly rates for all affected roles',
    '3. Fill "Process Baseline Metrics" tab - current state metrics for each process',
    '4. Complete "Opportunity Inputs" tab - expected benefits and costs per opportunity',
    '5. Add "Non-Financial Benefits" tab - qualitative impacts not captured in ROI',
    '',
    'DATA CONFIDENCE LEVELS',
    '• HIGH: Based on system data, time studies, or verified historical records',
    '• MEDIUM: Based on interviews with experienced staff or partial data',
    '• LOW: Estimates or assumptions requiring validation',
    '',
    'FIELD DEFINITIONS BY TAB',
    '',
    'ENGAGEMENT SETUP',
    '• Client Name*: Organization name',
    '• Engagement Name*: Project identifier',
    '• Analyst Name*: Consultant completing template',
    '• Date Prepared*: Data collection date',
    '• Data Confidence Level*: Self-assessed reliability (High/Medium/Low)',
    '• Data Sources*: How metrics were gathered (e.g., "Interviews with Process Owners, SharePoint logs")',
    '',
    'LABOR ECONOMICS',
    '• Role Name*: Job title affected by automation (e.g., "Credit Analyst", "Compliance Officer")',
    '• Fully-Loaded Hourly Rate*: Salary + benefits + overhead ÷ annual hours (typically $75-$200/hr)',
    '• FTE Count*: Number of people in this role',
    '• Annual Working Hours*: Default 2,080 hours (40 hrs/week × 52 weeks)',
    '',
    'PROCESS BASELINE METRICS',
    '• Process Name*: Must match process name from Jarvis',
    '• Weekly Transaction Volume*: Number of cases/requests processed per week',
    '• Current Cycle Time (hours)*: End-to-end elapsed time from start to completion',
    '• Current FTE Hours/Week*: Total manual effort spent on this process weekly',
    '• Current Error Rate (%)*: Percentage of transactions requiring rework',
    '• Average Rework Time (hours)*: Time spent fixing each error',
    '• Variability Fields: Standard deviation for Monte Carlo simulation (default 10-30%)',
    '',
    'OPPORTUNITY-SPECIFIC INPUTS',
    '• Opportunity ID/Name*: Must match opportunity from Jarvis',
    '• Estimated Effort Reduction (%)*: Expected % reduction in manual hours (e.g., 60%)',
    '• Estimated Cycle Time Reduction (%)*: Expected % speedup (e.g., 75%)',
    '• Estimated Error Reduction (%)*: Expected % improvement in quality (e.g., 90%)',
    '• Implementation Cost - Software*: Licensing, tools, platforms',
    '• Implementation Cost - Services*: External consulting or development',
    '• Implementation Cost - Internal*: Client team time (hours × hourly rate)',
    '• Ongoing Annual Cost*: Maintenance, licenses, support',
    '• Implementation Timeline (weeks)*: Expected duration',
    '• Range Fields: Conservative/optimistic estimates for Monte Carlo',
    '',
    'NON-FINANCIAL BENEFITS',
    '• Employee Experience Impact: How automation affects staff (morale, career development)',
    '• Customer Experience Impact: External benefits (faster response, fewer errors)',
    '• Risk/Compliance Improvement: Audit trail, regulatory alignment',
    '• Strategic Alignment: Connection to company priorities',
    '',
    'TIPS FOR DATA GATHERING',
    '• Interview process owners AND practitioners (managers may not know actual times)',
    '• Use system logs/reports where available (SharePoint analytics, CRM reports)',
    '• Conduct time studies for 1-2 weeks to validate estimates',
    '• Document assumptions clearly in Notes fields',
    '• Start conservative - easier to justify upward revisions than defend inflated projections',
    '',
    'NEXT STEPS AFTER COMPLETION',
    '1. Review all required (*) fields are completed',
    '2. Validate data with client stakeholders',
    '3. Upload template to Jarvis (Settings > Import Metrics)',
    '4. Jarvis will recalculate all business cases using your grounded data',
    '',
    'FOR SUPPORT',
    'Contact your Jarvis administrator or refer to the user guide'
  ];

  instructions.forEach((text, index) => {
    sheet.getCell(`A${index + 2}`).value = text;
    if (text.startsWith('•')) {
      sheet.getCell(`A${index + 2}`).font = { size: 11 };
    } else if (text === text.toUpperCase() && text.length > 0) {
      sheet.getCell(`A${index + 2}`).font = { bold: true, size: 12 };
    }
  });

  sheet.getColumn(1).width = 120;
}

/**
 * Tab 1: Engagement Setup
 */
function createEngagementSetupTab(workbook: ExcelJS.Workbook) {
  const sheet = workbook.addWorksheet('Engagement Setup');

  // Row 1: Headers
  const headerRow = sheet.getRow(1);
  const headers = ['Client Name*', 'Engagement Name*', 'Analyst Name*', 'Date Prepared*', 'Data Confidence Level*', 'Data Sources*', 'Notes'];

  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });

  headerRow.height = 30;

  // Row 2: Descriptions/Examples
  const descRow = sheet.getRow(2);
  const descriptions = [
    'Organization name',
    'Project identifier',
    'Consultant completing template',
    'YYYY-MM-DD',
    'High / Medium / Low',
    'How metrics were gathered',
    'Additional context'
  ];

  descriptions.forEach((desc, index) => {
    const cell = descRow.getCell(index + 1);
    cell.value = desc;
    cell.font = { italic: true, size: 9, color: { argb: 'FF666666' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
  });

  descRow.height = 25;

  // Row 3: Data entry row with defaults
  const dataRow = sheet.getRow(3);
  dataRow.getCell(4).value = new Date(); // Date Prepared
  dataRow.getCell(4).numFmt = 'yyyy-mm-dd';
  dataRow.getCell(5).value = 'High'; // Default confidence level

  // Data validation for Data Confidence Level
  sheet.getCell('E3').dataValidation = {
    type: 'list',
    allowBlank: false,
    formulae: ['"High,Medium,Low"'],
    showErrorMessage: true,
    errorTitle: 'Invalid Selection',
    error: 'Please select High, Medium, or Low'
  };

  // Column widths
  sheet.getColumn(1).width = 25; // Client Name
  sheet.getColumn(2).width = 25; // Engagement Name
  sheet.getColumn(3).width = 20; // Analyst Name
  sheet.getColumn(4).width = 15; // Date Prepared
  sheet.getColumn(5).width = 22; // Data Confidence Level
  sheet.getColumn(6).width = 40; // Data Sources
  sheet.getColumn(7).width = 30; // Notes
}

/**
 * Tab 2: Labor Economics
 */
function createLaborEconomicsTab(workbook: ExcelJS.Workbook) {
  const sheet = workbook.addWorksheet('Labor Economics');

  // Row 1: Headers
  const headerRow = sheet.getRow(1);
  const headers = ['Role Name*', 'Fully-Loaded Hourly Rate ($)*', 'FTE Count*', 'Annual Working Hours*', 'Notes'];

  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF70AD47' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });

  headerRow.height = 30;

  // Row 2: Descriptions/Examples
  const descRow = sheet.getRow(2);
  const descriptions = [
    'e.g., Credit Analyst',
    'Salary + benefits ÷ hours',
    'Number of people',
    'Default: 2080',
    'Role-specific notes'
  ];

  descriptions.forEach((desc, index) => {
    const cell = descRow.getCell(index + 1);
    cell.value = desc;
    cell.font = { italic: true, size: 9, color: { argb: 'FF666666' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
  });

  descRow.height = 25;

  // Add 3 data rows with defaults (starting from row 3)
  for (let i = 3; i <= 5; i++) {
    const row = sheet.getRow(i);
    row.getCell(2).numFmt = '$#,##0.00'; // Currency format
    row.getCell(3).numFmt = '0'; // Integer format
    row.getCell(4).value = 2080; // Default annual hours
    row.getCell(4).numFmt = '0';
  }

  // Column widths
  sheet.getColumn(1).width = 30;
  sheet.getColumn(2).width = 30;
  sheet.getColumn(3).width = 12;
  sheet.getColumn(4).width = 22;
  sheet.getColumn(5).width = 50;
}

/**
 * Tab 3: Process Baseline Metrics
 */
function createProcessBaselineMetricsTab(workbook: ExcelJS.Workbook, processNames: string[]) {
  const sheet = workbook.addWorksheet('Process Baseline Metrics');

  // Headers
  const headerRow = sheet.getRow(1);
  const headers = [
    'Process Name*',
    'Weekly Transaction Volume*',
    'Volume Variability',
    'Current Cycle Time (hrs)*',
    'Cycle Time Variability',
    'Current FTE Hours/Week*',
    'FTE Hours Variability',
    'Primary Role(s)*',
    'Current Error Rate (%)*',
    'Error Rate Variability',
    'Avg Rework Time (hrs)*',
    'Customer Impact',
    'Compliance Risk Level',
    'Data Source Notes*'
  ];

  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC000' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });

  headerRow.height = 40;

  // Row 2: Descriptions/Examples
  const descRow = sheet.getRow(2);
  const descriptions = [
    'From Jarvis',
    'Cases/week',
    'Std dev (0.15)',
    'Hours end-to-end',
    'Std dev (0.20)',
    'Manual hours/week',
    'Std dev (0.10)',
    'Comma-separated',
    'Rework %',
    'Std dev (0.25)',
    'Hours per error',
    'Qualitative',
    'High/Medium/Low/None',
    'Data sources used'
  ];

  descriptions.forEach((desc, index) => {
    const cell = descRow.getCell(index + 1);
    cell.value = desc;
    cell.font = { italic: true, size: 9, color: { argb: 'FF666666' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF4E6' } };
  });

  descRow.height = 30;

  // Add data rows (starting from row 3)
  const numRows = Math.max(processNames.length, 3);
  for (let i = 0; i < numRows; i++) {
    const row = sheet.getRow(i + 3);

    // Process name (pre-filled if available)
    if (i < processNames.length) {
      row.getCell(1).value = processNames[i];
    }

    // Default variability values
    row.getCell(3).value = 0.15; // Volume Variability
    row.getCell(3).numFmt = '0.00';
    row.getCell(5).value = 0.20; // Cycle Time Variability
    row.getCell(5).numFmt = '0.00';
    row.getCell(7).value = 0.10; // FTE Hours Variability
    row.getCell(7).numFmt = '0.00';
    row.getCell(10).value = 0.25; // Error Rate Variability
    row.getCell(10).numFmt = '0.00';

    // Number formats
    row.getCell(2).numFmt = '0'; // Integer
    row.getCell(4).numFmt = '0.00'; // Decimal
    row.getCell(6).numFmt = '0.00'; // Decimal
    row.getCell(9).numFmt = '0.00%'; // Percentage
    row.getCell(11).numFmt = '0.00'; // Decimal

    // Dropdown for Compliance Risk Level (column 13)
    row.getCell(13).value = 'Medium';
    row.getCell(13).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: ['"High,Medium,Low,None"']
    };
  }

  // Column widths
  const widths = [35, 20, 18, 22, 20, 22, 18, 25, 20, 18, 20, 30, 20, 40];
  widths.forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });
}

/**
 * Tab 4: Opportunity-Specific Inputs
 */
function createOpportunitySpecificInputsTab(workbook: ExcelJS.Workbook, opportunityNames: string[], processNames: string[]) {
  const sheet = workbook.addWorksheet('Opportunity Inputs');

  // Headers
  const headerRow = sheet.getRow(1);
  const headers = [
    'Opportunity ID*',
    'Opportunity Name*',
    'Linked Process*',
    'Effort Reduction (%)*',
    'Effort Range Low (%)',
    'Effort Range High (%)',
    'Cycle Time Reduction (%)*',
    'Cycle Range Low (%)',
    'Cycle Range High (%)',
    'Error Reduction (%)*',
    'Error Range Low (%)',
    'Error Range High (%)',
    'Implementation Cost - Software ($)*',
    'Implementation Cost - Services ($)*',
    'Implementation Cost - Internal ($)*',
    'Cost Variability',
    'Ongoing Annual Cost ($)*',
    'Implementation Timeline (weeks)*',
    'Timeline Variability',
    'Confidence Level*',
    'Assumptions & Notes*'
  ];

  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC00000' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });

  headerRow.height = 50;

  // Row 2: Descriptions/Examples
  const descRow = sheet.getRow(2);
  const descriptions = [
    'From Jarvis',
    'From Jarvis',
    'Parent process',
    '% reduction',
    'Conservative %',
    'Optimistic %',
    '% speedup',
    'Conservative %',
    'Optimistic %',
    '% quality gain',
    'Conservative %',
    'Optimistic %',
    'License, tools',
    'Consulting fees',
    'Internal hours × rate',
    'Std dev (0.25)',
    'Annual cost',
    'Weeks',
    'Std dev (0.30)',
    'High/Medium/Low',
    'Key assumptions'
  ];

  descriptions.forEach((desc, index) => {
    const cell = descRow.getCell(index + 1);
    cell.value = desc;
    cell.font = { italic: true, size: 9, color: { argb: 'FF666666' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE6E6' } };
  });

  descRow.height = 30;

  // Add data rows (starting from row 3)
  const numRows = Math.max(opportunityNames.length, 3);
  for (let i = 0; i < numRows; i++) {
    const row = sheet.getRow(i + 3);

    // Pre-fill opportunity data
    if (i < opportunityNames.length) {
      row.getCell(1).value = `OPP-${i + 1}`;
      row.getCell(2).value = opportunityNames[i];
      if (processNames.length > 0) {
        row.getCell(3).value = processNames[0];
      }
    }

    // Percentage formats
    row.getCell(4).numFmt = '0.00%';
    row.getCell(5).numFmt = '0.00%';
    row.getCell(6).numFmt = '0.00%';
    row.getCell(7).numFmt = '0.00%';
    row.getCell(8).numFmt = '0.00%';
    row.getCell(9).numFmt = '0.00%';
    row.getCell(10).numFmt = '0.00%';
    row.getCell(11).numFmt = '0.00%';
    row.getCell(12).numFmt = '0.00%';

    // Currency formats
    row.getCell(13).numFmt = '$#,##0.00';
    row.getCell(14).numFmt = '$#,##0.00';
    row.getCell(15).numFmt = '$#,##0.00';
    row.getCell(17).numFmt = '$#,##0.00';

    // Default variability values
    row.getCell(16).value = 0.25; // Cost Variability
    row.getCell(16).numFmt = '0.00';
    row.getCell(19).value = 0.30; // Timeline Variability
    row.getCell(19).numFmt = '0.00';

    // Integer format for timeline
    row.getCell(18).numFmt = '0';

    // Dropdown for Confidence Level (column 20)
    row.getCell(20).value = 'Medium';
    row.getCell(20).dataValidation = {
      type: 'list',
      allowBlank: false,
      formulae: ['"High,Medium,Low"']
    };
  }

  // Column widths
  const widths = [15, 35, 20, 18, 18, 18, 20, 16, 16, 18, 16, 16, 25, 25, 25, 16, 20, 22, 18, 16, 50];
  widths.forEach((width, index) => {
    sheet.getColumn(index + 1).width = width;
  });
}

/**
 * Tab 5: Non-Financial Benefits
 */
function createNonFinancialBenefitsTab(workbook: ExcelJS.Workbook, opportunityNames: string[]) {
  const sheet = workbook.addWorksheet('Non-Financial Benefits');

  // Headers
  const headerRow = sheet.getRow(1);
  const headers = [
    'Opportunity ID',
    'Employee Experience Impact',
    'Customer Experience Impact',
    'Risk/Compliance Improvement',
    'Strategic Alignment',
    'Scalability'
  ];

  headers.forEach((header, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = header;
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5B9BD5' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });

  headerRow.height = 30;

  // Row 2: Descriptions/Examples
  const descRow = sheet.getRow(2);
  const descriptions = [
    'From Tab 4',
    'Staff morale, career impact',
    'Faster response, fewer errors',
    'Audit trail, regulatory',
    'Strategic priorities',
    'Future volume handling'
  ];

  descriptions.forEach((desc, index) => {
    const cell = descRow.getCell(index + 1);
    cell.value = desc;
    cell.font = { italic: true, size: 9, color: { argb: 'FF666666' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F2FF' } };
  });

  descRow.height = 25;

  // Add data rows (starting from row 3)
  const numRows = Math.max(opportunityNames.length, 3);
  for (let i = 0; i < numRows; i++) {
    const row = sheet.getRow(i + 3);
    if (i < opportunityNames.length) {
      row.getCell(1).value = `OPP-${i + 1}`;
    }
  }

  // Column widths
  sheet.getColumn(1).width = 15;
  sheet.getColumn(2).width = 40;
  sheet.getColumn(3).width = 40;
  sheet.getColumn(4).width = 40;
  sheet.getColumn(5).width = 40;
  sheet.getColumn(6).width = 40;
}
